(() => {
  if (customElements.get('bb-bestel-flow')) return;

  class BbBestelFlow extends HTMLElement {
    connectedCallback() {
      if (this.ready) return;
      this.ready = true;
      try { this.data = JSON.parse(this.querySelector('[data-bb-bestel-data]').textContent); }
      catch (_error) { return; }
      this.fixedChoice = this.dataset.fixedChoice === 'beterbeaumpje';
      if (this.fixedChoice) {
        this.data.locations = this.data.locations.filter((location) =>
          location.with_clump?.variants?.some((variant) => this.isFixedVariant(variant)));
      }
      this.form = this.querySelector('form');
      this.summary = this.querySelector('.bb-bestel__summary');
      this.totalLabel = this.querySelector('[data-summary-total-label]').textContent.trim();
      this.summaryToggle = this.querySelector('[data-summary-toggle]');
      this.summaryToggle.addEventListener('click', () => {
        const expanded = this.summary.dataset.summaryExpanded !== 'true';
        this.summary.dataset.summaryExpanded = String(expanded);
        this.summaryToggle.setAttribute('aria-expanded', String(expanded));
      });
      this.summary.addEventListener('keydown', (event) => {
        if (event.key === 'Escape' && this.summary.dataset.summaryExpanded === 'true') {
          this.summary.dataset.summaryExpanded = 'false';
          this.summaryToggle.setAttribute('aria-expanded', 'false');
          this.summaryToggle.focus();
        }
      });
      this.citySelect = this.querySelector('[data-city-select]');
      this.locationSelect = this.querySelector('[data-location-select]');
      this.state = { delivery: '', city: '', location: '', family: '', ticket: '', variant: '', cross: false, quantity: 1, crossQuantity: 1, ticketQuantity: 1 };
      this.applyPrefill();
      this.applyFixedSelection();
      this.locationRecord = null;
      this.locationDaysReady = false;
      this.pickupDayLabels = [];
      this.locationNonce = 0;
      this.crossEligible = false;
      this.crossEligibilityKnown = false;
      this.crossNonce = 0;
      this.loading = false;
      this.money = new Intl.NumberFormat(document.documentElement.lang || 'nl-NL', { style: 'currency', currency: window.Shopify?.currency?.active || 'EUR' });
      this.priceNumber = new Intl.NumberFormat(document.documentElement.lang || 'nl-NL', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
      this.wholeNumber = new Intl.NumberFormat(document.documentElement.lang || 'nl-NL', { maximumFractionDigits: 0 });
      this.labels = this.data.labels || {};
      this.buildCities();
      this.form.addEventListener('change', (event) => this.onChange(event));
      this.form.addEventListener('submit', (event) => { event.preventDefault(); this.submit(); });
      this.classList.add('is-ready');
      this.render();
    }

    buildCities() {
      const cities = [...new Set(this.data.locations.map((location) => location.city))];
      cities.forEach((city) => this.citySelect.add(new Option(city, city)));
    }

    applyPrefill() {
      const params = new URLSearchParams(window.location.search);
      const delivery = params.get('delivery');
      const family = params.get('family');
      if (!['pickup', 'shipping'].includes(delivery)) return;
      this.state.delivery = delivery;
      if (!['with_clump', 'without_clump'].includes(family)) return;
      this.prefillFamily = family;
      this.prefillSize = this.sizeKey(params.get('size'));
      this.prefillKind = ['regular', 'premium'].includes(params.get('kind')) ? params.get('kind') : 'regular';
      if (delivery === 'shipping' && this.data.shipping?.[family]?.variants?.some((variant) => variant.available)) {
        this.state.family = family;
        this.selectPrefilledVariant();
      }
    }

    sizeKey(value) {
      const match = String(value || '').match(/(\d{2,3})\s*[-–]\s*(\d{2,3})/);
      return match ? `${match[1]}-${match[2]}` : '';
    }

    variantKind(variant) {
      return /beterbeaumpje/i.test(variant.treeName || '') || /bbplus/i.test(variant.sku || '') ? 'premium' : 'regular';
    }

    isFixedVariant(variant) {
      return variant.available && this.sizeKey(variant.option || variant.title) === '175-200'
        && this.variantKind(variant) === 'premium';
    }

    applyFixedSelection() {
      if (!this.fixedChoice) return;
      this.state.family = 'with_clump';
      const variants = this.state.delivery ? this.variants() : [];
      this.state.variant = variants.length === 1 ? String(variants[0].id) : '';
    }

    selectPrefilledVariant() {
      if (!this.prefillSize || !this.state.family) return;
      const matches = this.variants().filter((variant) =>
        this.sizeKey(variant.option || variant.title) === this.prefillSize
        && this.variantKind(variant) === this.prefillKind);
      if (matches.length === 1) this.state.variant = String(matches[0].id);
    }

    onChange(event) {
      this.stopScroll();
      const target = event.target;
      const state = this.state;
      let shouldLoadLocation = false;
      const shouldCheckCross = target.name === 'family' || target.name === 'size';
      if (target.name === 'delivery') {
        Object.assign(state, { delivery: target.value, city: '', location: '', family: '', ticket: '', variant: '', cross: false });
        this.prefillFamily = '';
        this.prefillSize = '';
        this.locationRecord = null;
        this.locationDaysReady = false;
        this.pickupDayLabels = [];
        this.locationNonce += 1;
      } else if (target === this.citySelect) {
        Object.assign(state, { city: target.value, location: '', family: '', ticket: '', variant: '', cross: false });
        this.locationRecord = null;
        this.locationDaysReady = false;
        this.pickupDayLabels = [];
        this.locationNonce += 1;
      } else if (target === this.locationSelect) {
        Object.assign(state, { location: target.value, family: '', ticket: '', variant: '', cross: false });
        if (this.prefillFamily && this.location()?.[this.prefillFamily]?.variants?.some((variant) => variant.available)) {
          state.family = this.prefillFamily;
          this.selectPrefilledVariant();
        }
        this.locationRecord = null;
        this.locationDaysReady = false;
        this.pickupDayLabels = [];
        this.locationNonce += 1;
        shouldLoadLocation = Boolean(target.value);
      } else if (target.name === 'family') {
        Object.assign(state, { family: target.value, ticket: '', variant: '', cross: false });
        this.prefillFamily = '';
        this.prefillSize = '';
      } else if (target.name === 'ticket') {
        Object.assign(state, { ticket: target.value, variant: '', cross: false });
        this.selectPrefilledVariant();
      } else if (target.name === 'size') {
        Object.assign(state, { variant: target.value, cross: false });
        this.prefillSize = '';
      } else if (target.matches('[data-cross-checkbox]')) {
        state.cross = target.checked;
      } else if (target.name === 'quantity') {
        state.quantity = Number(target.value);
      } else if (target.name === 'cross_quantity') {
        state.crossQuantity = Number(target.value);
      } else if (target.name === 'ticket_quantity') {
        state.ticketQuantity = Number(target.value);
      }
      this.applyFixedSelection();
      if (target.name === 'delivery' || target === this.citySelect || target === this.locationSelect || shouldCheckCross) {
        this.crossEligible = false;
        this.crossEligibilityKnown = false;
        this.crossNonce += 1;
      }
      this.querySelector('[data-error]').hidden = true;
      if (target.name === 'delivery' || target === this.citySelect || target === this.locationSelect) {
        this.querySelector('[data-location-loading]').hidden = true;
      }
      this.render();
      if (!shouldLoadLocation) this.scrollToNext(this.nextScrollTarget(target));
      if (shouldLoadLocation) this.loadLocation();
      else if (shouldCheckCross) this.checkCross();
    }

    nextScrollTarget(target) {
      if (target.name === 'delivery') {
        const next = target.value === 'pickup' ? 'pickup' : this.fixedChoice ? 'ticket' : 'family';
        return this.querySelector(`[data-step="${next}"]`);
      }
      if (target === this.citySelect) return this.locationSelect;
      if (target.name === 'family') return this.querySelector(`[data-step="${this.state.delivery === 'shipping' && target.value === 'with_clump' ? 'ticket' : 'size'}"]`);
      if (target.name === 'ticket') return this.querySelector(`[data-step="${this.fixedChoice ? 'quantity' : 'size'}"]`);
      if (target.name === 'size') {
        const cross = this.querySelector('[data-step="cross"]');
        return cross.hidden ? this.querySelector('[data-step="quantity"]') : cross;
      }
      if (target.matches('[data-cross-checkbox]')) return this.querySelector('[data-step="quantity"]');
      return null;
    }

    scrollToNext(target) {
      if (!target) return;
      this.stopScroll();
      this.scrollFrame = requestAnimationFrame(() => {
        this.scrollFrame = null;
        if (!this.isConnected || target.closest('[hidden]') || target.disabled) return;
        this.cancelSmoothScroll = window.BBSoftScroll?.to(target);
      });
    }

    stopScroll() {
      if (this.scrollFrame) cancelAnimationFrame(this.scrollFrame);
      this.scrollFrame = null;
      this.cancelSmoothScroll?.();
      this.cancelSmoothScroll = null;
    }

    disconnectedCallback() { this.stopScroll(); }

    location() { return this.data.locations.find((location) => location.handle === this.state.location) || null; }
    product() {
      if (!this.state.family) return null;
      return this.state.delivery === 'pickup' ? this.location()?.[this.state.family] : this.data.shipping[this.state.family];
    }
    variants() {
      return (this.product()?.variants || []).filter((variant) =>
        this.fixedChoice ? this.isFixedVariant(variant) : variant.available);
    }
    variant() { return this.variants().find((variant) => String(variant.id) === this.state.variant) || null; }
    sizeLabel(variant) {
      const value = String(variant.option || variant.title || '');
      const match = value.match(/(\d{2,3})\s*[-–]\s*(\d{2,3})/);
      const size = match ? `${match[1]}–${match[2]} cm` : value;
      return this.variantKind(variant) === 'premium' ? `BeterBeaumpje · ${size}` : size;
    }
    isReady() {
      const { delivery, city, location, family, ticket, quantity } = this.state;
      if (!delivery || !family || !this.variant() || quantity < 1 || quantity > 4) return false;
      if (delivery === 'pickup' && (!city || !location || !this.locationRecord || !this.locationDaysReady)) return false;
      if (delivery === 'shipping' && family === 'with_clump' && !ticket) return false;
      if (ticket === 'yes' && !this.data.ticket?.available) return false;
      if (this.state.cross && !this.variant()?.cross?.available) return false;
      if (this.state.cross && delivery === 'pickup' && !this.crossEligible) return false;
      if (this.state.cross && (!Number.isInteger(this.state.crossQuantity) || this.state.crossQuantity < 1 || this.state.crossQuantity > 4)) return false;
      if (ticket === 'yes' && (!Number.isInteger(this.state.ticketQuantity) || this.state.ticketQuantity < 1 || this.state.ticketQuantity > 4)) return false;
      return true;
    }

    render() {
      const state = this.state;
      const pickup = state.delivery === 'pickup';
      const shipping = state.delivery === 'shipping';
      const location = this.location();
      const afterLocation = shipping || (pickup && location);
      const ticketStep = shipping && state.family === 'with_clump';
      const afterTicket = !ticketStep || Boolean(state.ticket);
      const variant = this.variant();

      this.querySelector('[data-step="pickup"]').hidden = !pickup;
      this.querySelector('[data-step="family"]').hidden = this.fixedChoice || !afterLocation;
      this.querySelector('[data-step="ticket"]').hidden = !(afterLocation && ticketStep);
      this.querySelector('[data-step="size"]').hidden = this.fixedChoice || !(afterLocation && state.family && afterTicket);
      this.querySelector('[data-step="cross"]').hidden = !(variant && state.family === 'without_clump' && variant.cross?.available);
      this.querySelector('[data-step="quantity"]').hidden = !variant || (this.fixedChoice && (!afterTicket || pickup && !this.locationDaysReady));
      this.querySelector('[data-deposit-note]').hidden = !(pickup && state.family === 'with_clump' && this.product()?.depositCents > 0);
      this.querySelector('[data-pickup-empty]').hidden = !(pickup && this.data.locations.length === 0);
      this.citySelect.value = state.city;
      const availableLocations = this.data.locations.filter((item) => item.city === state.city);
      this.locationSelect.replaceChildren(new Option(this.labels.chooseLocation || 'Kies je locatie', ''));
      availableLocations.forEach((item) => this.locationSelect.add(new Option(item.name, item.handle)));
      this.locationSelect.disabled = !state.city;
      this.locationSelect.value = state.location;

      this.querySelectorAll('[data-family]').forEach((label) => {
        const family = label.dataset.family;
        const product = pickup ? location?.[family] : this.data.shipping[family];
        const input = label.querySelector('input');
        input.disabled = !product?.variants?.some((item) => item.available);
        input.checked = state.family === family;
        label.hidden = !product;
      });
      this.querySelectorAll('input[name="delivery"]').forEach((input) => { input.checked = state.delivery === input.value; });
      if (this.fixedChoice) {
        this.querySelector('input[name="delivery"][value="shipping"]').disabled = !this.data.shipping?.with_clump?.variants?.some((item) => this.isFixedVariant(item));
      }
      this.querySelectorAll('input[name="ticket"]').forEach((input) => { input.checked = state.ticket === input.value; });
      this.querySelector('[data-ticket-yes] input').disabled = !this.data.ticket?.available;
      this.querySelector('[data-ticket-price]').textContent = this.data.ticket ? `+ ${this.money.format(this.data.ticket.price / 100)}` : '';
      this.querySelector('[data-size-options]').replaceChildren(...this.variants().map((item) => this.sizeOption(item)));
      this.querySelector('[data-size-empty]').hidden = this.variants().length > 0;
      const crossInput = this.querySelector('[data-cross-checkbox]');
      crossInput.checked = state.cross;
      crossInput.disabled = !variant?.cross?.available || (pickup && !this.crossEligible);
      this.querySelector('[data-cross-detail]').textContent = variant?.cross
        ? pickup && !this.crossEligible
          ? this.crossEligibilityKnown ? this.labels.crossUnavailable : this.labels.crossChecking
          : `+ ${this.money.format(variant.cross.price / 100)}`
        : '';
      this.querySelectorAll('input[name="quantity"]').forEach((input) => { input.checked = Number(input.value) === state.quantity; });
      this.querySelector('[data-cross-quantity-wrap]').hidden = !state.cross;
      this.querySelector('[data-ticket-quantity-wrap]').hidden = state.ticket !== 'yes';
      this.querySelector('[name="cross_quantity"]').value = String(state.crossQuantity);
      this.querySelector('[name="ticket_quantity"]').value = String(state.ticketQuantity);

      this.text('[data-summary-delivery]', shipping ? this.labels.shipping : pickup ? this.labels.pickup : '—');
      this.text('[data-summary-location]', pickup ? location?.name || '—' : '—');
      this.querySelectorAll('[data-summary-location-row]').forEach((row) => { row.hidden = !pickup; });
      this.querySelectorAll('[data-summary-days-row]').forEach((row) => { row.hidden = !pickup || !this.locationDaysReady; });
      this.text('[data-summary-days]', this.pickupDayLabels.join(', ') || '—');
      this.text('[data-summary-family]', state.family === 'with_clump' ? this.labels.withClump : state.family === 'without_clump' ? this.labels.withoutClump : '—');
      this.text('[data-summary-size]', variant ? this.sizeLabel(variant) : '—');
      this.text('[data-summary-quantity]', state.quantity);
      this.text('[data-summary-compact-product]', variant
        ? this.variantKind(variant) === 'premium' ? this.sizeLabel(variant)
          : `BeterBoompje ${state.family === 'with_clump' ? 'met kluit' : 'zonder kluit'} · ${this.sizeLabel(variant)}`
        : this.labels.stepInitial || 'Jouw keuze');
      this.text('[data-mobile-summary]', pickup
        ? location?.name ? `Ophalen: ${location.name}` : this.labels.pickup
        : shipping ? this.labels.shipping
          : `${this.labels.step || 'Stap'} 1 ${this.labels.of || 'van'} ${this.fixedChoice ? 3 : 4}`);
      const addOns = [state.cross ? `${this.labels.cross} × ${state.crossQuantity}` : '', state.ticket === 'yes' ? `${this.labels.ticket} × ${state.ticketQuantity}` : ''].filter(Boolean);
      this.querySelectorAll('[data-summary-addon-row]').forEach((row) => { row.hidden = !addOns.length; });
      this.text('[data-summary-addon]', addOns.join(' · '));
      const total = variant ? variant.price * state.quantity
        + (state.cross ? variant.cross.price * state.crossQuantity : 0)
        + (state.ticket === 'yes' ? (this.data.ticket?.price || 0) * state.ticketQuantity : 0) : null;
      const depositTotal = variant && pickup && state.family === 'with_clump'
        ? (this.product()?.depositCents || 0) * state.quantity : 0;
      this.text('[data-summary-total-label]', depositTotal ? 'Prijs' : this.totalLabel);
      this.text('[data-summary-total]', total === null ? '—' : this.priceNumber.format((total - depositTotal) / 100));
      this.querySelectorAll('[data-summary-deposit]').forEach((node) => { node.hidden = !depositTotal; });
      this.text('[data-summary-deposit]', depositTotal
        ? `+ ${this.wholeNumber.format(depositTotal / 100)} borg · terug bij inleveren` : '');
      let step;
      if (this.fixedChoice) {
        step = !state.delivery ? 1
          : (pickup && (!state.location || !this.locationDaysReady)) || (shipping && !state.ticket) ? 2 : 3;
      } else {
        step = !state.delivery ? 1 : !(shipping || state.location) || !state.family ? 2 : !variant ? 3 : 4;
      }
      const totalSteps = this.fixedChoice ? 3 : 4;
      this.text('[data-progress]', `${this.labels.step || 'Stap'} ${step} ${this.labels.of || 'van'} ${totalSteps}`);
      this.querySelector('[data-progress-bar]').style.width = `${step * 100 / totalSteps}%`;
      const ready = this.isReady();
      this.classList.toggle('has-selection', ready);
      this.querySelector('[data-finish]').hidden = !ready;
      this.querySelectorAll('[data-submit]').forEach((button) => {
        button.disabled = !ready || this.loading;
        button.textContent = this.loading ? this.labels.loading : this.labels.continue;
      });
    }

    sizeOption(variant) {
      const label = document.createElement('label');
      label.className = 'bb-bestel__choice bb-bestel__choice--size';
      const input = document.createElement('input');
      input.type = 'radio'; input.name = 'size'; input.value = String(variant.id);
      input.checked = this.state.variant === input.value;
      const body = document.createElement('span'); body.className = 'bb-bestel__choice-body';
      const name = document.createElement('strong'); name.textContent = this.sizeLabel(variant);
      const depositCents = this.state.delivery === 'pickup' && this.state.family === 'with_clump'
        ? this.product()?.depositCents || 0 : 0;
      const price = document.createElement('small');
      price.className = 'bb-bestel__choice-price';
      const treePrice = document.createElement('span');
      treePrice.textContent = this.priceNumber.format((variant.price - depositCents) / 100);
      price.append(treePrice);
      if (depositCents > 0) {
        const deposit = document.createElement('span');
        deposit.className = 'bb-bestel__choice-deposit';
        deposit.textContent = `+ ${this.wholeNumber.format(depositCents / 100)} borg`;
        price.append(deposit);
      }
      body.append(name, price); label.append(input, body);
      return label;
    }

    text(selector, value) { this.querySelectorAll(selector).forEach((node) => { node.textContent = value; }); }
    error(message) {
      const node = this.querySelector('[data-error]');
      node.textContent = message;
      node.hidden = false;
    }

    locationError(message, loading) {
      this.error(message);
      this.text('[data-location-status]', message);
      loading.classList.add('is-error');
      loading.hidden = false;
    }

    async loadLocation() {
      const location = this.location();
      const nonce = ++this.locationNonce;
      const info = this.querySelector('[data-location-info]');
      const loading = this.querySelector('[data-location-loading]');
      info.hidden = true;
      if (!location) return;
      loading.classList.remove('is-error');
      this.text('[data-location-status]', 'We controleren de ophaaldagen voor deze locatie…');
      if (!window.BbZapiet) { this.locationError(this.labels.locationUnclear, loading); return; }
      const selected = this.variant();
      const probes = this.fixedChoice && selected
        ? [{ product_id: this.product().id, variant_id: selected.id }]
        : location.probe || [];
      if (!probes.length) { this.locationError(this.labels.noTrees, loading); return; }
      loading.hidden = false;
      try {
        const matches = await window.BbZapiet.matchingLocations(this.dataset.shop, probes);
        if (nonce !== this.locationNonce) return;
        const usage = this.data.locations.filter((item) => item.handle !== location.handle && item.probe?.some((probe) => probes.some((own) => own.product_id === probe.product_id))).length;
        const record = window.BbZapiet.resolve(matches, location.zapietId, location.name, usage > 0);
        if (!record) { this.locationError(this.labels.locationUnclear, loading); return; }
        this.locationRecord = record;
        this.text('[data-location-name]', location.name);
        this.text('[data-location-address]', [record.address_line_1, record.address_line_2, record.postal_code, record.city].filter(Boolean).join(', '));
        this.render();
        const days = await window.BbZapiet.pickupDays(this.dataset.shop, record.id, probes);
        if (nonce !== this.locationNonce) return;
        if (!days.length) { this.locationError(this.labels.noDays, loading); return; }
        this.locationDaysReady = true;
        const formatted = days.map((day) => new Intl.DateTimeFormat(document.documentElement.lang || 'nl-NL', { weekday: 'short', day: 'numeric', month: 'short', timeZone: 'UTC' }).format(new Date(`${day}T12:00:00Z`)));
        this.pickupDayLabels = formatted;
        this.text('[data-location-dates]', `${this.labels.pickupDays}: ${formatted.join(', ')}. ${this.labels.chooseDateInCart}`);
        loading.hidden = true;
        info.hidden = false;
        this.render();
        this.checkCross();
      } catch (_error) {
        if (nonce === this.locationNonce) this.locationError(this.labels.locationUnclear, loading);
      }
    }

    async checkCross() {
      const variant = this.variant();
      if (this.state.delivery !== 'pickup' || !this.locationRecord || !variant?.cross?.available) return;
      const nonce = ++this.crossNonce;
      this.crossEligible = false;
      this.crossEligibilityKnown = false;
      this.render();
      try {
        const matches = await window.BbZapiet.matchingLocations(this.dataset.shop, [
          { product_id: this.product().id, variant_id: variant.id },
          { product_id: this.data.crossProductId, variant_id: variant.cross.id },
        ]);
        if (nonce !== this.crossNonce) return;
        this.crossEligible = matches.some((location) => String(location.id) === String(this.locationRecord.id));
      } catch (_error) {
        if (nonce !== this.crossNonce) return;
        this.crossEligible = false;
      }
      this.crossEligibilityKnown = true;
      this.render();
    }

    async submit() {
      if (!this.isReady() || this.loading) return;
      this.loading = true; this.render();
      const variant = this.variant();
      const quantity = this.state.quantity;
      const items = [{ id: Number(variant.id), quantity }];
      if (this.state.cross) items.push({ id: Number(variant.cross.id), quantity: this.state.crossQuantity });
      if (this.state.ticket === 'yes') items.push({
        id: Number(this.data.ticket.id),
        quantity: this.state.ticketQuantity,
        parent_id: Number(variant.id),
        properties: { _addon: 'terugplantticket' },
      });
      try {
        if (this.state.delivery === 'pickup') {
          const product = this.product();
          const probes = items.map((item, index) => ({ product_id: index === 0 ? product.id : this.data.crossProductId, variant_id: item.id }));
          const matches = await window.BbZapiet.matchingLocations(this.dataset.shop, probes);
          if (!matches.some((location) => String(location.id) === String(this.locationRecord.id))) throw new Error(this.labels.locationUnclear);
          const firstDay = await window.BbZapiet.firstPickupDay(this.dataset.shop, this.locationRecord.id, probes);
          if (!firstDay) throw new Error(this.labels.noDays);
        }
        const cartAddUrl = (window.routes?.cart_add_url || '/cart/add').replace(/\/cart\/add\/?$/, '/cart/add.js');
        const response = await fetch(cartAddUrl, {
          method: 'POST', headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
          body: JSON.stringify({ items }), credentials: 'same-origin',
        });
        if (!response.ok) throw new Error(this.labels.cartError);
        window.location.assign(window.routes?.cart_url || '/cart');
      } catch (error) {
        this.error(error.message || this.labels.cartError);
        this.loading = false; this.render();
      }
    }
  }
  customElements.define('bb-bestel-flow', BbBestelFlow);
})();
